import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import QRCode from "react-qr-code";
import { Alert, Button, Input, Label } from "reactstrap";
import notifications from "../../common/Components/Notifications/Notification";
import { canUseGiRegistry } from "../../helpers/geoEnabled";
import { canManageKarkhana } from "../../helpers/karkhanaEnabled";
import { getGi } from "../../services/geoGiApi";
import {
  bindLoomQr,
  checkGiContains,
  createLoom,
  deleteLoom,
  getLoom,
  listLoomPins,
  setLoomActive,
  updateLoom,
  uploadLoomPhoto,
} from "../../services/geoLoomApi";
import GiFenceMap from "../gi-registry/GiFenceMap";
import GiFrozenSelect from "../gi-registry/GiFrozenSelect";
import { parseGeoPath, unwrapGeoPayload } from "../gi-registry/geoPath";
import "../gi-registry/gi-registry.scss";
import GeoPhotoLightbox from "./GeoPhotoLightbox";
import { copyText, loomQrUrl } from "./loomQr";
import { stampLoomPhoto, GeoFix } from "./stampLoomPhoto";

const readAuthUser = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null") || {};
  } catch {
    return {};
  }
};

const stickerCode = (raw: string) => {
  const trimmed = String(raw || "").trim();
  if (!trimmed) {
    return "";
  }
  try {
    const parsed = new URL(trimmed);
    const last = parsed.pathname.split("/").filter(Boolean).pop();
    return last || trimmed;
  } catch {
    return trimmed;
  }
};

const isMobileDevice = () =>
  /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent || "");

type LoomViewKey = "FRONT" | "BACK" | "SIDE_LEFT" | "SIDE_RIGHT";

const LOOM_VIEWS: { key: LoomViewKey; label: string; required?: boolean }[] = [
  { key: "FRONT", label: "Front", required: true },
  { key: "BACK", label: "Back" },
  { key: "SIDE_LEFT", label: "Left side" },
  { key: "SIDE_RIGHT", label: "Right side" },
];

type ViewPhoto = {
  url: string;
  previewUrl?: string;
  capturedAt?: string;
  accuracyMeters?: number;
  stampText?: string;
  source?: string;
};

const emptyPhotos = (): Record<LoomViewKey, ViewPhoto | undefined> => ({
  FRONT: undefined,
  BACK: undefined,
  SIDE_LEFT: undefined,
  SIDE_RIGHT: undefined,
});

const LoomForm = () => {
  const history = useHistory();
  const location = useLocation();
  const path = parseGeoPath(location.pathname, "looms");
  const loomId = path.id;
  const isNew = path.isNew;
  const isEdit = path.isEdit;
  const readOnly = !isEdit;
  const authUser: any = readAuthUser();
  const role = authUser?.users?.role;

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [record, setRecord] = useState<any>(null);
  const [giId, setGiId] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");
  const [photos, setPhotos] = useState<Record<LoomViewKey, ViewPhoto | undefined>>(emptyPhotos);
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");
  const [notes, setNotes] = useState("");
  const [loomGroup, setLoomGroup] = useState("");
  const [loomType, setLoomType] = useState("");
  const [productionActive, setProductionActive] = useState(true);
  const [bindCode, setBindCode] = useState("");
  const [liveHull, setLiveHull] = useState<any>(null);
  const [pins, setPins] = useState<any[]>([]);
  const [check, setCheck] = useState<any>(null);
  const [geoError, setGeoError] = useState("");
  const [photoCapturedAt, setPhotoCapturedAt] = useState("");
  const [photoAccuracyMeters, setPhotoAccuracyMeters] = useState<number | undefined>();
  const [photoSource, setPhotoSource] = useState("");
  const [photoStampText, setPhotoStampText] = useState("");
  const [liveReady, setLiveReady] = useState(false);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const galleryInputRef = useRef<HTMLInputElement | null>(null);
  const liveFixRef = useRef<GeoFix | null>(null);
  const previewUrlsRef = useRef<string[]>([]);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const webcamStreamRef = useRef<MediaStream | null>(null);
  const captureViewRef = useRef<LoomViewKey>("FRONT");
  const [webcamOpen, setWebcamOpen] = useState(false);
  const [activeView, setActiveView] = useState<LoomViewKey>("FRONT");
  const [lightboxIndex, setLightboxIndex] = useState(-1);

  const applyLoom = (view: any) => {
    setRecord(view);
    setGiId(view?.giId || "");
    const next = emptyPhotos();
    if (Array.isArray(view?.photos)) {
      view.photos.forEach((photo: any) => {
        const key = String(photo?.view || "").toUpperCase() as LoomViewKey;
        if (key && photo?.url && key in next) {
          next[key] = {
            url: photo.url,
            capturedAt: photo.capturedAt ? new Date(photo.capturedAt).toISOString() : undefined,
            accuracyMeters: Number.isFinite(Number(photo.accuracyMeters))
              ? Number(photo.accuracyMeters)
              : undefined,
            stampText: photo.stampText || undefined,
            source: photo.source || undefined,
          };
        }
      });
    }
    if (!next.FRONT && view?.photoUrl) {
      next.FRONT = {
        url: view.photoUrl,
        capturedAt: view?.photoCapturedAt ? new Date(view.photoCapturedAt).toISOString() : undefined,
        accuracyMeters: Number.isFinite(Number(view?.photoAccuracyMeters))
          ? Number(view.photoAccuracyMeters)
          : undefined,
        stampText: view?.photoStampText || undefined,
        source: view?.photoSource || undefined,
      };
    }
    setPhotos(next);
    setPhotoUrl(next.FRONT?.url || view?.photoUrl || "");
    setLat(view?.lat === 0 || view?.lat ? String(view.lat) : "");
    setLng(view?.lng === 0 || view?.lng ? String(view.lng) : "");
    setNotes(view?.notes || "");
    setLoomGroup(view?.loomGroup || "");
    setLoomType(view?.loomType || "");
    setProductionActive(view?.productionActive !== false);
    setBindCode(view?.qrCode || "");
    setPhotoCapturedAt(next.FRONT?.capturedAt || "");
    setPhotoAccuracyMeters(next.FRONT?.accuracyMeters);
    setPhotoSource(next.FRONT?.source || "");
    setPhotoStampText(next.FRONT?.stampText || "");
  };

  const loadLoom = async (id: string) => {
    setLoading(true);
    try {
      const res: any = await getLoom(id);
      const view = unwrapGeoPayload(res);
      if (!res?.success || !view) {
        notifications({
          title: "Error!",
          message: res?.message || "Loom not found",
          type: "danger",
        });
        history.replace("/looms");
        return;
      }
      applyLoom(view);
    } catch {
      notifications({
        title: "Error!",
        message: "Unable to load loom",
        type: "danger",
      });
      history.replace("/looms");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canUseGiRegistry(role)) {
      history.replace("/dashboard");
      return;
    }
    if (!isNew && loomId) {
      loadLoom(loomId);
    }
  }, [loomId, isNew]);

  useEffect(() => {
    if (!navigator.geolocation) {
      return;
    }
    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        liveFixRef.current = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracyM: pos.coords.accuracy,
          source: "LIVE",
          capturedAt: new Date(pos.timestamp || Date.now()),
        };
        setLiveReady(true);
      },
      () => {
        setLiveReady(false);
      },
      { enableHighAccuracy: true, maximumAge: 8000, timeout: 20000 }
    );
    return () => navigator.geolocation.clearWatch(watchId);
  }, []);

  useEffect(() => {
    return () => {
      previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      webcamStreamRef.current?.getTracks().forEach((track) => track.stop());
    };
  }, []);

  useEffect(() => {
    if (!webcamOpen) {
      return;
    }
    const video = videoRef.current;
    const stream = webcamStreamRef.current;
    if (!video || !stream) {
      return;
    }
    video.srcObject = stream;
    video.play().catch(() => undefined);
  }, [webcamOpen]);

  const stopWebcam = () => {
    const stream = webcamStreamRef.current;
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }
    webcamStreamRef.current = null;
    const video = videoRef.current;
    if (video) {
      video.pause();
      video.srcObject = null;
    }
    setWebcamOpen(false);
  };

  const openLaptopCamera = async () => {
    setGeoError("");
    stopWebcam();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 1280 } },
        audio: false,
      });
      webcamStreamRef.current = stream;
      setWebcamOpen(true);
    } catch {
      setGeoError("Could not open the laptop camera. Choose a photo instead.");
      galleryInputRef.current?.click();
    }
  };

  const captureWebcam = async () => {
    const video = videoRef.current;
    if (!video || video.videoWidth < 2) {
      setGeoError("Camera is not ready yet. Try again.");
      return;
    }
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }
    ctx.drawImage(video, 0, 0);
    const blob: Blob | null = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", 0.9)
    );
    if (!blob) {
      setGeoError("Could not capture that frame");
      return;
    }
    stopWebcam();
    await handlePhoto(new File([blob], "loom-webcam.jpg", { type: "image/jpeg" }), captureViewRef.current);
  };

  const openTakePhoto = (view: LoomViewKey = activeView) => {
    captureViewRef.current = view;
    setActiveView(view);
    if (isMobileDevice()) {
      cameraInputRef.current?.click();
      return;
    }
    openLaptopCamera();
  };

  useEffect(() => {
    if (!giId) {
      setLiveHull(null);
      setPins([]);
      return;
    }
    getGi(giId).then((res: any) => {
      if (res?.success) {
        setLiveHull(res?.data?.liveHull || null);
      }
    });
    listLoomPins(giId).then((res: any) => {
      if (res?.success && Array.isArray(res.data)) {
        setPins(res.data);
      }
    });
  }, [giId]);

  useEffect(() => {
    const latN = Number(lat);
    const lngN = Number(lng);
    if (!giId || !Number.isFinite(latN) || !Number.isFinite(lngN)) {
      setCheck(null);
      return;
    }
    const timer = window.setTimeout(async () => {
      const res: any = await checkGiContains(giId, latN, lngN);
      if (res?.success) {
        setCheck(res.data);
      } else {
        setCheck(null);
      }
    }, 350);
    return () => window.clearTimeout(timer);
  }, [giId, lat, lng]);

  const draftPin = useMemo(() => {
    const latN = Number(lat);
    const lngN = Number(lng);
    if (!Number.isFinite(latN) || !Number.isFinite(lngN)) {
      return null;
    }
    return { lat: latN, lng: lngN };
  }, [lat, lng]);

  const outside = check && check.inside === false;
  const savedId = record?.id || record?._id || loomId;

  const handlePhoto = async (file?: File, view: LoomViewKey = captureViewRef.current) => {
    if (!file) {
      return;
    }
    stopWebcam();
    setGeoError("");
    setSaving(true);
    try {
      const typedLat = Number(lat);
      const typedLng = Number(lng);
      const typedFix: GeoFix | null =
        Number.isFinite(typedLat) && Number.isFinite(typedLng)
          ? { lat: typedLat, lng: typedLng, source: "LIVE", capturedAt: new Date() }
          : null;
      const stamped = await stampLoomPhoto(file, liveFixRef.current || typedFix);
      previewUrlsRef.current.push(stamped.previewUrl);
      const url = await uploadLoomPhoto(stamped.file);
      const nextPhoto: ViewPhoto = {
        url,
        previewUrl: stamped.previewUrl,
        capturedAt: stamped.capturedAt,
        accuracyMeters: stamped.accuracyM,
        stampText: stamped.stampText,
        source: stamped.source,
      };
      setPhotos((current) => ({ ...current, [view]: nextPhoto }));
      if (view === "FRONT") {
        setPhotoUrl(url);
        setLat(String(stamped.lat));
        setLng(String(stamped.lng));
        setPhotoCapturedAt(stamped.capturedAt);
        setPhotoAccuracyMeters(stamped.accuracyM);
        setPhotoSource(stamped.source);
        setPhotoStampText(stamped.stampText);
      }
      notifications({
        title: "Success!",
        message: stamped.source === "EXIF"
          ? `${view === "FRONT" ? "Front" : "Photo"} stamped from the image GPS`
          : `${view === "FRONT" ? "Front" : "Photo"} stamped with current location`,
        type: "success",
      });
    } catch (err: any) {
      setGeoError(err?.message || "Could not stamp this photo");
      notifications({
        title: "Error!",
        message: err?.message || "Could not upload photo",
        type: "danger",
      });
    } finally {
      setSaving(false);
    }
  };

  const useCurrentLocation = () => {
    setGeoError("");
    if (!navigator.geolocation) {
      setGeoError("This browser cannot share location. Type lat/lng instead.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLat(String(pos.coords.latitude));
        setLng(String(pos.coords.longitude));
      },
      () => {
        setGeoError("Could not read current location. Type lat/lng instead.");
      },
      { enableHighAccuracy: true, timeout: 12000 }
    );
  };

  const persist = async () => {
    const latN = Number(lat);
    const lngN = Number(lng);
    if (!giId) {
      notifications({ title: "Error!", message: "Select a GI", type: "danger" });
      return;
    }
    if (!photos.FRONT?.url && !photoUrl) {
      notifications({ title: "Error!", message: "Add a front photo of the loom", type: "danger" });
      return;
    }
    if (!Number.isFinite(latN) || !Number.isFinite(lngN)) {
      notifications({
        title: "Error!",
        message: "Use current location or enter lat/lng",
        type: "danger",
      });
      return;
    }
    setSaving(true);
    try {
      const body = {
        giId,
        photoUrl: photos.FRONT?.url || photoUrl,
        photos: LOOM_VIEWS.map(({ key }) => {
          const photo = photos[key];
          if (!photo?.url) {
            return null;
          }
          return {
            view: key,
            url: photo.url,
            capturedAt: photo.capturedAt,
            accuracyMeters: photo.accuracyMeters,
            stampText: photo.stampText,
            source: photo.source,
          };
        }).filter(Boolean),
        lat: latN,
        lng: lngN,
        notes: notes.trim() || undefined,
        qrCode: stickerCode(bindCode) || undefined,
        ...(canManageKarkhana(role)
          ? {
              loomGroup: loomGroup.trim() || "",
              loomType: loomType.trim() || "",
              productionActive,
            }
          : {}),
        photoCapturedAt: photos.FRONT?.capturedAt || photoCapturedAt || undefined,
        photoAccuracyMeters: photos.FRONT?.accuracyMeters ?? photoAccuracyMeters,
        photoStampText: photos.FRONT?.stampText || photoStampText || undefined,
        photoSource: photos.FRONT?.source || photoSource || undefined,
      };
      const res: any = isNew
        ? await createLoom(body)
        : await updateLoom(savedId as string, body);
      if (!res?.success) {
        notifications({
          title: "Error!",
          message: res?.message || "Could not save loom",
          type: "danger",
        });
        return;
      }
      const saved = unwrapGeoPayload(res);
      if (saved) {
        applyLoom(saved);
      }
      notifications({
        title: "Success!",
        message: res?.message || "Loom tagged",
        type: "success",
      });
      const nextId = saved?.id || saved?._id;
      if (isNew && nextId) {
        history.replace(`/looms/${nextId}/edit`);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleBindQr = async () => {
    if (!savedId || !stickerCode(bindCode)) {
      notifications({
        title: "Error!",
        message: "Enter the printed QR code",
        type: "danger",
      });
      return;
    }
    setSaving(true);
    try {
      const res: any = await bindLoomQr(savedId as string, stickerCode(bindCode));
      if (!res?.success) {
        notifications({
          title: "Error!",
          message: res?.message || "Could not bind QR",
          type: "danger",
        });
        return;
      }
      applyLoom(unwrapGeoPayload(res));
      notifications({
        title: "Success!",
        message: "Printed QR bound",
        type: "success",
      });
    } finally {
      setSaving(false);
    }
  };

  const removePhoto = (view: LoomViewKey) => {
    if (view === "FRONT") {
      return;
    }
    setPhotos((current) => ({ ...current, [view]: undefined }));
  };

  const handleToggleActive = async () => {
    if (!loomId) {
      return;
    }
    const nextActive = record?.active === false;
    setSaving(true);
    try {
      const res: any = await setLoomActive(loomId, nextActive);
      if (!res?.success) {
        notifications({
          title: "Error!",
          message: res?.message || "Could not update loom status",
          type: "danger",
        });
        return;
      }
      const view = unwrapGeoPayload(res);
      if (view) {
        applyLoom(view);
      }
      notifications({
        title: "Success!",
        message: nextActive ? "Loom marked active" : "Loom marked inactive",
        type: "success",
      });
    } catch {
      notifications({
        title: "Error!",
        message: "Could not update loom status",
        type: "danger",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!loomId) {
      return;
    }
    if (!window.confirm(`Delete loom ${record?.loomCode || ""}? This cannot be undone.`)) {
      return;
    }
    setSaving(true);
    try {
      const res: any = await deleteLoom(loomId);
      if (!res?.success) {
        notifications({
          title: "Error!",
          message: res?.message || "Could not delete this loom",
          type: "danger",
        });
        return;
      }
      notifications({
        title: "Success!",
        message: "Loom deleted",
        type: "success",
      });
      history.push("/looms");
    } catch {
      notifications({
        title: "Error!",
        message: "Could not delete this loom",
        type: "danger",
      });
    } finally {
      setSaving(false);
    }
  };

  const formLightboxItems = LOOM_VIEWS.flatMap(({ key, label }) => {
    const photo = photos[key];
    const url = photo?.previewUrl || photo?.url;
    return url ? [{ url, label }] : [];
  });

  const copyLoomUrl = async () => {
    if (!record?.loomCode) {
      return;
    }
    const url = loomQrUrl(record.loomCode);
    const ok = await copyText(url);
    notifications({
      title: ok ? "Success!" : "Error!",
      message: ok ? "Loom URL copied" : "Could not copy the URL",
      type: ok ? "success" : "danger",
    });
  };

  if (loading) {
    return <div className="gi-registry-page">Loading loom…</div>;
  }

  return (
    <div className="gi-registry-page loom-form">
      <div className="gi-registry-toolbar">
        <h2>{isNew ? "New loom" : record?.loomCode || "Loom details"}</h2>
        <div className="gi-form-actions" style={{ marginTop: 0 }}>
          <Button className="ResetBtn" onClick={() => history.push("/looms")}>
            Back to list
          </Button>
          {readOnly && loomId ? (
            <Button
              className="btn-hastkala-outline"
              onClick={() => history.push(`/looms/${loomId}/edit`)}
            >
              Edit
            </Button>
          ) : null}
          {!isNew && loomId ? (
            <>
              <Button
                className="btn-hastkala-outline"
                disabled={saving}
                onClick={handleToggleActive}
              >
                {record?.active === false ? "Mark active" : "Mark inactive"}
              </Button>
              <Button className="btn-hastkala-outline" disabled={saving} onClick={handleDelete}>
                Delete
              </Button>
            </>
          ) : null}
        </div>
      </div>
      {record?.active === false ? (
        <div className="gi-form-banner">This loom is inactive. Public scans still open, with an inactive notice.</div>
      ) : null}
      <div className="gi-form-layout">
        <div className="gi-form-fields">
          <GiFrozenSelect
            value={giId}
            onChange={setGiId}
            required
            label="GI"
            disabled={readOnly}
            fallbackLabel={record?.giName}
          />
          <Label>Loom photos</Label>
          <p className="loom-hint">
            Front is required and sets the location. The other views are optional.
          </p>
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            {...(isMobileDevice() ? { capture: "environment" as const } : {})}
            className="loom-geocam-file"
            onChange={(e) => {
              handlePhoto(e.target.files?.[0], captureViewRef.current);
              e.target.value = "";
            }}
          />
          <input
            ref={galleryInputRef}
            type="file"
            accept="image/jpeg,image/png,image/heic,.jpg,.jpeg,.png,.heic"
            className="loom-geocam-file"
            onChange={(e) => {
              handlePhoto(e.target.files?.[0], captureViewRef.current);
              e.target.value = "";
            }}
          />
          <div className="loom-photo-grid">
            {LOOM_VIEWS.map((slot) => {
              const photo = photos[slot.key];
              const src = photo?.previewUrl || photo?.url;
              return (
                <div className="loom-photo-slot" key={slot.key}>
                  <div className="loom-photo-slot__label">
                    {slot.label}
                    {slot.required ? " *" : ""}
                  </div>
                  <button
                    type="button"
                    className="loom-geocam-shutter loom-geocam-shutter--slot"
                    disabled={saving || (readOnly && !src)}
                    onClick={() => {
                      if (src) {
                        stopWebcam();
                        const idx = formLightboxItems.findIndex((item) => item.label === slot.label);
                        setLightboxIndex(idx >= 0 ? idx : 0);
                        return;
                      }
                      if (!readOnly) {
                        openTakePhoto(slot.key);
                      }
                    }}
                    aria-label={src ? `View ${slot.label} photo` : `Take ${slot.label} photo`}
                  >
                    {src ? (
                      <img src={src} alt={`${slot.label} of loom`} />
                    ) : (
                      <span className="loom-geocam-shutter__empty">
                        <span className="loom-geocam-shutter__ring" />
                        <span>Take photo</span>
                      </span>
                    )}
                  </button>
                  {!readOnly ? (
                    <div className="loom-photo-slot__actions">
                      <Button
                        type="button"
                        className="ResetBtn"
                        disabled={saving}
                        onClick={() => openTakePhoto(slot.key)}
                      >
                        {src ? "Replace" : "Camera"}
                      </Button>
                      <Button
                        type="button"
                        className="btn-hastkala-outline"
                        disabled={saving}
                        onClick={() => {
                          stopWebcam();
                          captureViewRef.current = slot.key;
                          setActiveView(slot.key);
                          galleryInputRef.current?.click();
                        }}
                      >
                        Choose
                      </Button>
                      {src && !slot.required ? (
                        <Button
                          type="button"
                          className="btn-hastkala-outline"
                          disabled={saving}
                          onClick={() => removePhoto(slot.key)}
                        >
                          Remove
                        </Button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
          {webcamOpen ? (
            <div className="loom-webcam">
              <p className="loom-hint">
                Capturing {LOOM_VIEWS.find((slot) => slot.key === activeView)?.label || "photo"}. Tap the picture to take it.
              </p>
              <video
                ref={videoRef}
                playsInline
                autoPlay
                muted
                className="loom-webcam__video"
                onClick={captureWebcam}
              />
              <div className="gi-form-actions" style={{ marginTop: 8 }}>
                <Button className="ResetBtn" onClick={captureWebcam} disabled={saving}>
                  Capture
                </Button>
                <Button className="btn-hastkala-outline" onClick={stopWebcam}>
                  Cancel camera
                </Button>
              </div>
            </div>
          ) : null}
          <p className="loom-hint">
            Photos are stamped with date, time and GPS.
            {liveReady ? " Location is on." : " Allow location so the stamp can use it."}
          </p>
          {photoStampText ? (
            <p className="text-muted" style={{ fontSize: 13 }}>
              Front: {photoStampText}
              {photoSource ? ` · ${photoSource === "EXIF" ? "from photo" : "from device"}` : ""}
            </p>
          ) : null}
          <Button
            type="button"
            className="btn-hastkala-outline mb-2"
            onClick={useCurrentLocation}
            disabled={saving || readOnly}
          >
            Use current location
          </Button>
          {geoError ? <p className="text-danger" style={{ fontSize: 13 }}>{geoError}</p> : null}
          <div className="loom-coords">
            <Input
              type="number"
              inputMode="decimal"
              enterKeyHint="done"
              step="any"
              placeholder="Latitude"
              aria-label="Latitude"
              value={lat}
              readOnly={readOnly}
              onChange={(e) => setLat(e.target.value)}
            />
            <Input
              type="number"
              inputMode="decimal"
              enterKeyHint="done"
              step="any"
              placeholder="Longitude"
              aria-label="Longitude"
              value={lng}
              readOnly={readOnly}
              onChange={(e) => setLng(e.target.value)}
            />
          </div>
          <p className="loom-hint">
            Filled from the photo. Type them if location is off.
          </p>
          {outside ? (
            <Alert color="warning">{check.distanceLabel || "Outside the GI fence. You can still save."}</Alert>
          ) : check?.inside ? (
            <Alert color="success">{check.distanceLabel}</Alert>
          ) : null}
          <Label>Notes (optional)</Label>
          <Input type="textarea" rows={3} value={notes} readOnly={readOnly} onChange={(e) => setNotes(e.target.value)} />
          {canManageKarkhana(role) ? (
            <>
              <Label>Loom group (Karkhana)</Label>
              <Input
                value={loomGroup}
                readOnly={readOnly}
                onChange={(e) => setLoomGroup(e.target.value)}
                placeholder="N-block"
              />
              <Label className="mt-2">Loom type (Karkhana)</Label>
              <Input
                value={loomType}
                readOnly={readOnly}
                onChange={(e) => setLoomType(e.target.value)}
                placeholder="Pit / frame"
              />
              <Label className="mt-2">Use in Karkhana production</Label>
              <Input
                type="select"
                value={productionActive ? "yes" : "no"}
                disabled={readOnly}
                onChange={(e) => setProductionActive(e.target.value === "yes")}
              >
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </Input>
            </>
          ) : null}
          <Label>Bind sticker QR</Label>
          <p className="loom-hint">
            Paste a sticker code, or save and print the QR for this loom.
          </p>
          <Input
            value={bindCode}
            readOnly={readOnly}
            onChange={(e) => setBindCode(e.target.value)}
            placeholder="Code on the pre-printed sticker"
          />
          {isEdit && !isNew && record?.loomCode ? (
            <Button className="ResetBtn mt-2" disabled={saving} onClick={handleBindQr}>
              Bind sticker QR
            </Button>
          ) : null}
          {record?.loomCode ? (
            <div className="gi-form-banner is-info mt-3 loom-print-block">
              <p>
                Loom ID <strong>{record.loomCode}</strong>
              </p>
              <button
                type="button"
                className="mb-2 loom-qr"
                onClick={copyLoomUrl}
                title="Copy loom URL"
                aria-label="Copy loom URL"
              >
                <QRCode value={loomQrUrl(record.loomCode)} size={148} />
              </button>
              <p className="text-muted" style={{ fontSize: 13 }}>
                {loomQrUrl(record.loomCode)} · click the QR to copy
              </p>
              <Button className="ResetBtn loom-print-hide" onClick={() => window.print()}>
                Print QR
              </Button>
              <div className="loom-print-sheet">
                <div className="loom-qr-print">
                  <QRCode value={loomQrUrl(record.loomCode)} size={192} />
                </div>
                <p className="loom-print-caption">{record.loomCode}</p>
              </div>
            </div>
          ) : (
            <p className="loom-hint">
              Save the loom to generate its QR. A sticker code entered now is linked on save.
            </p>
          )}
          <div className="gi-form-actions loom-save-bar">
            {isEdit ? (
              <Button className="ResetBtn" disabled={saving} onClick={persist}>
                {isNew ? "Save loom" : "Update loom"}
              </Button>
            ) : null}
            <Button className="ResetBtn loom-save-back" onClick={() => history.push("/looms")}>
              Back to list
            </Button>
          </div>
        </div>
        <GiFenceMap
          liveHull={liveHull}
          looms={pins}
          draftPin={draftPin}
          hint="Approved fence only. Green pins are inside; amber pins are outside. Still not the legal GI map."
        />
      </div>
      <GeoPhotoLightbox
        items={formLightboxItems}
        index={lightboxIndex}
        onClose={() => setLightboxIndex(-1)}
        onIndex={setLightboxIndex}
      />
    </div>
  );
};

export default LoomForm;
